import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import "./AboutPage.css";
import aboutHeroImg from "../../img/icons/about-hero-img.png";

const AboutPage = () => {
  const { user } = useContext(UserContext);
  const [openGuideline, setOpenGuideline] = useState(null);
  const [expandedProblem, setExpandedProblem] = useState(null);

  const toggleProblem = (index) => {
    setExpandedProblem(expandedProblem === index ? null : index);
  };

  const toggleGuideline = (index) => {
    setOpenGuideline(openGuideline === index ? null : index);
  };

  const guidelines = [
    {
      title: "Contributions",
      icon: "bi-gift",
      content: (
        <>
          <p>There are multiple ways you can become a Move n Muse community contributor:</p>
          <ul>
            <li><strong>Leave elaborate event reviews</strong> - Help others make choices rooted in awareness by sharing your detailed experiences at dance events.</li>
            <li><strong>Add new upcoming or past events</strong> - Help grow our directory by adding dance events that aren't yet listed.</li>
            <li><strong>Give people Kudos</strong> - Uplift community members by recognizing their positive contributions to the dance floor and community.</li>
            <li><strong>Flag inappropriate content</strong> - Help protect our environment and uphold respect by reporting content that doesn't align with our values.</li>
            <li><strong>Write blog articles</strong> - Share your dance journey, insights, and stories to inspire and educate fellow dancers.</li>
            <li><strong>Share product recommendations</strong> - Build a library of dance-related products and share helpful recommendations with the community.</li>
          </ul>
          <p>Every contribution, big or small, helps build the community we all want to be part of.</p>
        </>
      )
    },
    {
      title: "Creating Dance Events",
      icon: "bi-calendar-plus",
      content: (
        <>
          <p>Event organizers and community members can add dance events to our directory. Here's how the process works:</p>
          <ul>
            <li><strong>Submit event details</strong> - Provide comprehensive information including dates, location, dance styles, instructors, and pricing.</li>
            <li><strong>Accuracy is key</strong> - Please ensure all information is as accurate as possible. Misleading event details negatively affect the community's trust.</li>
            <li><strong>Keep it updated</strong> - If event details change, update the listing promptly to keep dancers informed.</li>
            <li><strong>Admin review</strong> - New events may be reviewed by our team to ensure quality and accuracy before appearing publicly.</li>
          </ul>
          <p>Together, we're building the most comprehensive dance event directory available.</p>
        </>
      )
    },
    {
      title: "Reviews",
      icon: "bi-chat-quote",
      content: (
        <>
          <p><strong>All reviews on Move n Muse are anonymous.</strong> This design choice encourages transparency and honest feedback while reducing flattery and bias.</p>
          <p>However, anonymity is not a license for disrespect:</p>
          <ul>
            <li><strong>Be constructive</strong> - Share feedback that helps both dancers and organizers improve.</li>
            <li><strong>Stay respectful</strong> - Crude, offensive, or personal attacks will be flagged by the community and may be removed.</li>
            <li><strong>Be specific</strong> - Detailed reviews are more helpful than vague complaints or praise.</li>
            <li><strong>Remember the purpose</strong> - Reviews exist for awareness and information sharing, helping dancers make informed decisions about their next event.</li>
          </ul>

          <h4>Review Submission Window</h4>
          <p>Reviews can be submitted for <strong>up to 12 months after an event ends</strong>. This policy ensures:</p>
          <ul>
            <li><strong>Relevance</strong> - Reviews remain timely and useful for the community.</li>
            <li><strong>Accuracy</strong> - Memories of the event are still fresh enough to provide meaningful feedback.</li>
            <li><strong>Fairness</strong> - Organizers and venues have a reasonable timeframe to receive and respond to feedback.</li>
          </ul>
          <p>After 12 months, the review period closes and you will no longer be able to submit a review for that event.</p>

          <h4>Review Editing Policy</h4>
          <p>Reviews can be edited for <strong>one week after submission</strong>. This time-limited editing window is designed to:</p>
          <ul>
            <li><strong>Allow corrections</strong> - Fix typos, clarify points, or add details you may have forgotten.</li>
            <li><strong>Encourage reflection</strong> - Emotions can run high right after an event. The edit window gives you time to refine your thoughts while they're still fresh.</li>
            <li><strong>Minimize emotional reactions</strong> - We've found that reviews written in the heat of the moment may not reflect your true experience. Having time to edit helps ensure your feedback is thoughtful and fair.</li>
            <li><strong>Preserve authenticity</strong> - After one week, reviews become permanent to maintain the integrity of our community feedback.</li>
          </ul>
          <p>Your honest, thoughtful reviews help the entire community grow.</p>
        </>
      )
    },
    {
      title: "Kudos",
      icon: "bi-hand-thumbs-up",
      content: (
        <>
          <p>Kudos are our way of celebrating the special connections that happen on and off the dance floor. They exist to recognize the people who make your experience memorable.</p>
          <p><strong>How Kudos work:</strong></p>
          <ul>
            <li><strong>Attendees only</strong> - Only people who attended the same event can give each other Kudos. This ensures authentic recognition based on real interactions.</li>
            <li><strong>Anonymity protection</strong> - Kudos can only be given once 50 attendees have confirmed their attendance at an event. This threshold protects the anonymity of community members who share their experiences.</li>
            <li><strong>One Kudos per person</strong> - You can give Kudos to each attendee only once per event, keeping the system fair and meaningful.</li>
            <li><strong>Anonymous giving</strong> - Like reviews, Kudos are given anonymously to encourage genuine appreciation without social pressure.</li>
          </ul>
          <p><strong>How many Kudos can you give?</strong></p>
          <p>Everyone starts with 5 Kudos per event. If you create special experiences for many and receive Kudos in return, we encourage that by giving you more to share - 7 Kudos at 30 received, and 10 Kudos at 70 received. The more love you put out, the more love you get to spread.</p>
          <p>Spread the love - recognize the dancers, instructors, and community members who made your experience special!</p>
        </>
      )
    },
    {
      title: "Flags & Content Moderation",
      icon: "bi-flag",
      content: (
        <>
          <p>At Move n Muse, we take pride in fostering a dynamic and inclusive dance community. It's our collective responsibility to keep our space welcoming and safe, where respect is the rhythm to our dance.</p>

          <h4>What You Can Flag</h4>
          <p>Festivals, Reviews, Users, and Kudos</p>

          <h4>When to Flag Content</h4>
          <p>Flag content on Move n Muse when it:</p>
          <ul>
            <li>Offends, belittles, or harasses any individual or group</li>
            <li>Spreads false information or engages in deceptive practices</li>
            <li>Violates someone's privacy or shares personal details without consent</li>
            <li>Is considered spam or disrupts the community experience</li>
            <li>Promotes illegal activities or contravenes local laws</li>
          </ul>

          <h4>The Flagging Process</h4>
          <ol>
            <li><strong>Flagging</strong> - Use the flag icon to report content.</li>
            <li><strong>Comment</strong> - Provide a brief comment to give context to your flag, assisting our admin team in understanding the nuances of your concern.</li>
            <li><strong>Review</strong> - Carefully assess whether the content truly violates Move n Muse's community standards.</li>
            <li><strong>Threshold System</strong> - Content flagged by 3 distinct users will be temporarily hidden and queued for admin review.*</li>
            <li><strong>Admin Review</strong> - Our team evaluates the content impartially to determine adherence to community guidelines.</li>
          </ol>

          <h4>Our Commitment to Fair Moderation</h4>
          <ul>
            <li>Objective review is our cornerstone, ensuring impartiality in our decisions.</li>
            <li>We aim for prompt content reviews, appreciating your patience for thorough evaluations.</li>
            <li>Transparency guides us, and we endeavor to provide insights on content decisions.</li>
          </ul>

          <h4>Consequences of Misuse</h4>
          <ul>
            <li>Inappropriate flagging, such as for personal disagreements, can lead to restrictions or penalties for the flagger.</li>
            <li>Repeat offenses of flagging misuse may result in further actions.</li>
          </ul>

          <p><strong>Community Support:</strong> We rely on your engagement to uphold Move n Muse's standards through judicious flagging.</p>

          <h4>Boycott Reports</h4>
          <p>Beyond flagging specific content, Move n Muse provides a way for community members to express broader concerns about events through our <strong>Boycott Report</strong> feature.</p>
          <ul>
            <li><strong>Express concerns</strong> - If you have significant concerns about an event's practices, safety, or alignment with community values, you can submit a boycott report.</li>
            <li><strong>Moderator review</strong> - Our team of moderators carefully analyzes all boycott reports to understand the nature and validity of the concerns raised.</li>
            <li><strong>Community communication</strong> - When appropriate, moderators may share relevant concerns with event organizers and other attendees to promote transparency and awareness.</li>
            <li><strong>Constructive dialogue</strong> - The goal is not to harm events, but to encourage accountability and positive change within our community.</li>
          </ul>
          <p>This feature exists to give dancers a voice when they believe an event may not be serving the community's best interests. Use it thoughtfully and responsibly.</p>

          <h4>Content Removal Requests</h4>
          <p>If you are mentioned in a review or any content on Move n Muse and believe it contains false or defamatory claims about you:</p>
          <ul>
            <li><strong>If you have a Move n Muse account:</strong> Use the flag icon on the content and select <em>"This review mentions me - request takedown."</em> The content will be hidden immediately and placed at the top of our admin review queue.</li>
            <li><strong>If you do not have an account:</strong> Email us at <a href="mailto:contact@movenmuse.com">contact@movenmuse.com</a> with the subject line "Content Removal Request." Include a link to the content or a description of where it appears, and explain why you believe it should be removed.</li>
          </ul>
          <p>We aim to review all takedown requests within <strong>7 business days</strong>. Content found to violate our community guidelines or contain unsubstantiated claims about individuals will be removed. You will be notified of the outcome.</p>
        </>
      )
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section with Image */}
      <section className="hero-section about-hero-with-image">
        <div className="container">
          <div className="about-hero-content">
            <div className="about-hero-text">
              <h1 className="hero-title">The <span className="highlight-yellow">Move n Muse</span> Project</h1>
              <p className="hero-intro">
                At Move n Muse, we believe in the transformative power of social dance and its ability
                to bring joy, connection, and growth. This project was born with the mission of enriching
                the global social dance community by providing a space where every dancer's voice can be
                heard, every step can be celebrated, and every experience can be shared.
              </p>
            </div>
            <div className="about-hero-image">
              <img src={aboutHeroImg} alt="Move n Muse Community" />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-content">
            <h2 className="section-title">Our Story</h2>
            <div className="story-text">
              <p className="story-problem-heading">Move n Muse is a child of heartbreak.</p>

              <p>
                Far from home. In the capital of a particular dance style. Walking into an event room
                stripped of any warmth, joy, and kindness. The air filled with anxiety, desperation, lust.
                Smiles flickering here and there, often possible only by shutting down any empathy.
              </p>

              <p>
                A painful realization: the same dance that gave one a community and a safe haven
                from the madness of the world could also feel like this.
              </p>

              <p>
                Weeks of processing. More time searching for roots and understanding how common it is for a dance to lose its essence.
                Even more time looking for solutions.
              </p>

              <p>
                In the winter of 2023, the Move n Muse project was born. In the winter of 2026 it took a digital shape.
                A platform to share experiences with peers. To educate each other. To encourage and show gratitude.
              </p>
            </div>

            {/* The Problems — And Our Response */}
            <div className="story-problems">
              <p className="story-problem-heading" style={{ color: 'var(--text-dark)' }}>The problems - and our response</p>

              {/* Problem 1 */}
              <div className="story-problem">
                <button className="story-problem-toggle" onClick={() => toggleProblem(1)}>
                  <h4 className="story-problem-heading">• Experience disappears. We archive it.</h4>
                  <i className={`bi bi-chevron-${expandedProblem === 1 ? 'up' : 'down'}`}></i>
                </button>
                {expandedProblem === 1 && (
                <div className="story-problem-content">
                  <p>
                    Festivals are booked months in advance. Flights and passes are paid with savings,
                    vacation days, emotional courage and hope. We invest time, energy, money and trust.
                    And then it ends.
                  </p>
                  <p>
                    For years, there has been no central place to consolidate what dancers actually experience
                    at events. Knowledge travels through word of mouth, private messages, and short-lived
                    social media posts. These are not small investments - yet the learning disappears.
                  </p>
                  <p><strong>Our review feature changes that.</strong></p>
                  <p>By reviewing events, you turn lived experience into shared insight.</p>
                  <p className="story-poetic">
                    Not gossip.<br />
                    Not hype.<br />
                    Not cancellation.
                  </p>
                  <p>
                    Just thoughtful reflection so dancers can make informed decisions - and organizers can grow.
                  </p>
                </div>
                )}
              </div>

              {/* Problem 2 */}
              <div className="story-problem">
                <button className="story-problem-toggle" onClick={() => toggleProblem(2)}>
                  <h4 className="story-problem-heading">• Culture gets fragmented. We make it accessible.</h4>
                  <i className={`bi bi-chevron-${expandedProblem === 2 ? 'up' : 'down'}`}></i>
                </button>
                {expandedProblem === 2 && (
                <div className="story-problem-content">
                  <p>
                    History, ethics, power dynamics, expectations - these conversations rarely have enough
                    space in workshops or festival venues. Instead, they scatter across hundreds of influencers
                    and personal brands.
                  </p>
                  <p>We believe knowledge should not depend on who you follow.</p>
                  <p><strong>The Blog Library is the space where we contemplate deeply or let someone else absorb us by their thought.</strong></p>
                  <p className="story-poetic">
                    It's where beginners can build a strong foundation.<br />
                    It's where experienced dancers can question, refine, and deepen their understanding.<br />
                    It's where anyone - regardless of followers, titles, or visibility - can share reflections.
                  </p>
                </div>
                )}
              </div>

              {/* Problem 3 */}
              <div className="story-problem">
                <button className="story-problem-toggle" onClick={() => toggleProblem(3)}>
                  <h4 className="story-problem-heading">• Recognition is rare. We make it simple and fun.</h4>
                  <i className={`bi bi-chevron-${expandedProblem === 3 ? 'up' : 'down'}`}></i>
                </button>
                {expandedProblem === 3 && (
                <div className="story-problem-content">
                  <p>Growth thrives in recognition. But dance, like life, can be quick to critique and slow to appreciate.</p>
                  <p><strong>So we built Kudos.</strong></p>
                  <p>A simple, powerful way to say:</p>
                  <p className="story-poetic">
                    I see your effort.<br />
                    I felt your care.<br />
                    Thank you for the energy you brought to this dance floor.
                  </p>
                  <p>
                    Celebration is not boosting ego. It's recognizing and encouraging community contribution.
                  </p>
                </div>
                )}
              </div>
            </div>

            {/* Why We Exist */}
            <div className="story-why">
              <p>We are here to remind the community of itself.</p>
              <p className="story-poetic">
                To remove barriers to knowledge.<br />
                To protect collective memory.<br />
                To make room for culture.<br />
                To amplify encouragement.
              </p>
              <p>
                Because dance is not built by influencers.
                It is built by people on dance floors, in classes of your local instructors,
                in connection you hold with each of your dance partners.
              </p>
              <p>And when we learn from each other, we don't just dance better.</p>
              <p className="story-problem-heading">We thrive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <h2 className="section-title">Our Values</h2>
          <p className="section-subtitle values-intro">
            There is no doubt that some, if not all, of our values resonate with you because this is
            exactly what social dance teaches us. We therefore encourage everyone to practice these
            values on the Move n Muse platform.
          </p>

          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--dusty-rose), var(--warm-taupe))' }}>
                <i className="bi bi-heart"></i>
              </div>
              <h3 className="value-title">Kindness</h3>
              <p className="value-desc">
                We approach every interaction with kindness - towards yourself, others, your dance
                journey, and every dancer's story.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--sage-green), #D4DBA8)' }}>
                <i className="bi bi-lightbulb"></i>
              </div>
              <h3 className="value-title">Curiosity</h3>
              <p className="value-desc">
                A willingness to learn from others, question assumptions, and move beyond ego.
                Every dancer has something to teach us.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--warm-taupe), var(--dusty-rose))' }}>
                <i className="bi bi-shield-check"></i>
              </div>
              <h3 className="value-title">Respect</h3>
              <p className="value-desc">
                We believe in honest, thoughtful feedback that uplifts rather than harms.
                Every voice matters in our community.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--purple-light), var(--dusty-rose))' }}>
                <i className="bi bi-eye"></i>
              </div>
              <h3 className="value-title">Awareness</h3>
              <p className="value-desc">
                Knowing where you stand is where conscious movement begins.
                We encourage reflection and self-understanding.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--dusty-rose), var(--purple-light))' }}>
                <i className="bi bi-rocket-takeoff"></i>
              </div>
              <h3 className="value-title">Empowerment</h3>
              <p className="value-desc">
                Empowering dancers to reflect, speak, and move with intention.
                Your voice and choices matter.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon" style={{ background: 'linear-gradient(135deg, var(--sage-green), var(--warm-taupe))' }}>
                <i className="bi bi-people"></i>
              </div>
              <h3 className="value-title">Community</h3>
              <p className="value-desc">
                Together we build a culture of support and mutual care.
                We're stronger when we dance together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Guidelines Section */}
      <section className="guidelines-section">
        <div className="container">
          <h2 className="section-title">Community Guidelines</h2>
          <p className="section-subtitle">
            Everything you need to know about being part of Move n Muse
          </p>

          <div className="guidelines-accordion">
            {guidelines.map((guideline, index) => (
              <div
                key={index}
                className={`guideline-item ${openGuideline === index ? 'open' : ''}`}
              >
                <button
                  className="guideline-header"
                  onClick={() => toggleGuideline(index)}
                  aria-expanded={openGuideline === index}
                >
                  <div className="guideline-header-content">
                    <div className="guideline-icon">
                      <i className={`bi ${guideline.icon}`}></i>
                    </div>
                    <span className="guideline-title">{guideline.title}</span>
                  </div>
                  <i className={`bi ${openGuideline === index ? 'bi-chevron-up' : 'bi-chevron-down'} guideline-arrow`}></i>
                </button>
                <div className={`guideline-content ${openGuideline === index ? 'open' : ''}`}>
                  <div className="guideline-content-inner">
                    {guideline.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community CTA Section */}
      <section className="community-cta-section">
        <div className="container">
          <h2 className="community-cta-title">Join Our Community</h2>
          <p className="community-cta-text">
            Whether you want to discover new events, share your experiences through reviews,
            give Kudos to amazing dancers, or contribute to our blog - there's a place for you at Move n Muse.
            Join us and become part of a global dance community.
          </p>
          {!user ? (
            <Link to="/login?tab=signup" className="community-cta-btn">
              <i className="bi bi-person-plus"></i> Get Started
            </Link>
          ) : (
            <Link to="/events" className="community-cta-btn">
              <i className="bi bi-calendar-heart"></i> Explore Events
            </Link>
          )}
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
